import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, Globe, Award, Users, Target, Eye, ArrowRight, Wrench, Package, X, ZoomIn } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { AnimatedSection, SectionLabel, Seo } from "../components/ui/index";
import { cmsAPI, servicesAPI, bannersAPI, certificationsAPI } from "../services/api";
import { getImageUrl } from "../utils/image";

function safeParseJson(str, defaultValue) {
  if (!str) return defaultValue;
  let clean = str.replace(/<[^>]*>/g, "");
  clean = clean
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
  try {
    return JSON.parse(clean);
  } catch (e) {
    console.error("Failed to parse JSON setting:", clean, e);
    return defaultValue;
  }
}

const getIconComponent = (iconName) => {
  if (!iconName) return LucideIcons.HelpCircle;
  const formatted = iconName.charAt(0).toUpperCase() + iconName.slice(1);
  return LucideIcons[formatted] || LucideIcons[iconName] || LucideIcons.HelpCircle;
};
export { ContactPage } from "../pages/ContactPage";

// ── Banner Image Component ────────────────────────────────
function PageBanner({ page, fallbackClass = "from-brand-pale to-white", children }) {
  const [banner, setBanner] = useState(null);
  useEffect(() => {
    bannersAPI.get(page).then(r => { if (r.data.banner?.imageUrl) setBanner(r.data.banner); }).catch(() => {});
  }, [page]);

  if (banner) {
    return (
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0"><img src={banner.imageUrl} alt={banner.altText || page} className="w-full h-full object-cover" /><div className="absolute inset-0 bg-brand-primary/70 backdrop-blur-sm" /></div>
        <div className="container-custom relative z-10 text-white">{children}</div>
      </section>
    );
  }
  return (
    <section className={`bg-gradient-to-br ${fallbackClass} py-20`}>
      <div className="container-custom">{children}</div>
    </section>
  );
}

export function AboutPage() {
  const [cms, setCms] = useState({});
  const [certs, setCerts] = useState([]);
  const [viewCertItem, setViewCertItem] = useState(null);

  useEffect(() => {
    cmsAPI.getPage("about").then(r => setCms(r.data.content || {})).catch(() => {});
    certificationsAPI.getAll().then(r => setCerts(r.data.certifications || [])).catch(() => {});
  }, []);

  const g = (s, k, fb) => cms?.[s]?.[k]?.value || fb;

  return (
    <div className="pt-20">
      <Seo page="about" defaultTitle="About Us — Sava Fresh Trading FZE" defaultDesc="Learn about Sava Fresh Trading FZE — a premium global food trading company based in UAE's SAIF Zone." />
      <PageBanner page="about" fallbackClass="from-brand-pale via-white to-accent-tealLight">
        <AnimatedSection className="max-w-3xl">
          <SectionLabel text="About Us" />
          <h1 className={`section-heading mb-5`}>{g("hero","heading","About Sava Fresh Trading FZE")}</h1>
          <div className="text-gray-600 leading-relaxed text-lg rich-content" dangerouslySetInnerHTML={{ __html: g("hero","description","Sava Fresh Trading FZE is a premium global food trading company headquartered in the Sharjah Airport International Free Zone (SAIF Zone), UAE. With over a decade of experience in international food trade, we have built a reputation for quality, reliability, and transparent business practices. We connect certified agricultural producers with buyers across 50+ countries, offering a diverse range of food commodities with full supply chain support.") }} />
        </AnimatedSection>
      </PageBanner>

      {/* Story */}
      <section className="section-pad">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection direction="left">
              <SectionLabel text="Our Story" />
              <h2 className="section-heading mb-6">{g("story","heading","Built on Trust, Driven by Quality")}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: g("story", "paragraph1", "Founded with a vision to transform global food trade, Sava Fresh began as a regional food trading enterprise and has grown into a respected international trading company. Our journey is marked by strategic partnerships, quality certifications, and a relentless commitment to excellence at every stage of the supply chain.") }} />
                <div dangerouslySetInnerHTML={{ __html: g("story", "paragraph2", "Our deep relationships with certified producers across Asia, Africa, Europe, and the Americas allow us to source the finest quality food products at competitive prices. We handle everything from procurement and quality inspection to logistics, documentation, and final delivery — giving our clients a seamless, worry-free experience.") }} />
                <div dangerouslySetInnerHTML={{ __html: g("story", "paragraph3", "Today, we serve a diverse portfolio of clients including retail chains, wholesale distributors, food manufacturers, hotel groups, and government institutions across 50+ countries. Our growing team of trade specialists, quality assurance experts, and logistics professionals ensures every shipment meets the highest standards.") }} />
              </div>
            </AnimatedSection>
            <AnimatedSection direction="right" delay={200}>
              <div className="relative rounded-3xl overflow-hidden shadow-hover border-4 border-white bg-white group aspect-[4/3] lg:aspect-[1.2]">
                <img
                  src={getImageUrl(g("story", "imageUrl", "https://images.unsplash.com/photo-1521791136368-1a46827d0af1?auto=format&fit=crop&w=800&q=80"))}
                  alt="Our Story"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-pad bg-gray-section">
        <div className="container-custom">
          <AnimatedSection className="text-center mb-12"><SectionLabel text="Mission & Vision" color="gold"/><h2 className="section-heading">What Drives Us</h2></AnimatedSection>
          <div className="grid md:grid-cols-2 gap-6">
            <AnimatedSection delay={100}>
              <div className="bg-white rounded-3xl p-8 shadow-card border-l-4 border-brand-primary h-full hover:shadow-hover transition-all duration-300">
                <div className="w-14 h-14 bg-brand-pale rounded-2xl flex items-center justify-center mb-5"><Target className="w-7 h-7 text-brand-primary"/></div>
                <h3 className="font-heading font-bold text-gray-900 text-xl mb-3">Our Mission</h3>
                <div className="text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: g("mission","text","To be the world's most trusted food trading partner — connecting certified producers with global buyers through transparent, ethical, and efficient trade practices. We are committed to delivering premium quality products, ensuring complete regulatory compliance, and fostering long-term partnerships built on mutual respect and shared success.") }} />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="bg-white rounded-3xl p-8 shadow-card border-l-4 border-accent-gold h-full hover:shadow-hover transition-all duration-300">
                <div className="w-14 h-14 bg-accent-goldLight rounded-2xl flex items-center justify-center mb-5"><Eye className="w-7 h-7 text-accent-gold"/></div>
                <h3 className="font-heading font-bold text-gray-900 text-xl mb-3">Our Vision</h3>
                <div className="text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: g("vision","text","To become the leading global food trading company recognized for exceptional quality, ethical sourcing, and innovative supply chain solutions. We envision a future where Sava Fresh is the first choice for businesses seeking reliable, sustainable, and premium food products from around the world — setting new benchmarks for the industry.") }} />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Enhanced Section */}
      {g("enhanced_section", "heading", "") && (
        <section className="section-pad bg-brand-pale/30">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {g("enhanced_section", "imageUrl", "") && (
                <AnimatedSection direction="left" className="order-2 lg:order-1">
                  <div className="relative rounded-3xl overflow-hidden shadow-card border-4 border-white bg-white">
                    <img
                      src={g("enhanced_section", "imageUrl", "")}
                      alt={g("enhanced_section", "heading", "")}
                      className="w-full h-auto object-cover max-h-[450px]"
                    />
                  </div>
                </AnimatedSection>
              )}
              <AnimatedSection direction="right" className={`space-y-6 order-1 lg:order-2 ${g("enhanced_section", "imageUrl", "") ? "" : "lg:col-span-2 max-w-3xl mx-auto text-center"}`}>
                <SectionLabel text="Featured Highlight" />
                <h2 className="section-heading">{g("enhanced_section", "heading", "")}</h2>
                <div
                  className="rich-content text-gray-600 leading-relaxed text-sm"
                  dangerouslySetInnerHTML={{ __html: g("enhanced_section", "content", "") }}
                />
                {g("enhanced_section", "cta_text", "") && (
                  <div className={g("enhanced_section", "imageUrl", "") ? "" : "text-center pt-4"}>
                    <a
                      href={g("enhanced_section", "cta_link", "") || "/contact"}
                      className="btn-primary inline-flex"
                    >
                      {g("enhanced_section", "cta_text", "")} <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </AnimatedSection>
            </div>
          </div>
        </section>
      )}

      {/* Values */}
      <section className="section-pad">
        <div className="container-custom">
          <AnimatedSection className="text-center mb-12">
            <SectionLabel text={g("values", "label", "Our Values")} />
            <h2 className="section-heading">{g("values", "heading", "What We Stand For")}</h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-6">
            {(() => {
              const rawValuesJson = cms?.values?.values_json?.value;
              const parsedValues = rawValuesJson ? safeParseJson(rawValuesJson, null) : null;
              const valuesList = parsedValues || [
                {icon:"Award",title:"Quality First",desc:"We maintain uncompromising quality standards at every step of the supply chain. From supplier qualification to final delivery, quality is never compromised — it is our defining commitment.",color:"brand"},
                {icon:"Globe",title:"Global Reach",desc:"Our network spans 50+ countries across five continents. We leverage these global connections to source the finest products and deliver them to any market, efficiently and reliably.",color:"gold"},
                {icon:"Users",title:"Partnership",desc:"We build long-term relationships grounded in trust, transparency, and shared goals. Whether you are a producer or a buyer, we treat every partner as a valued member of the Sava Fresh family.",color:"brand"},
              ];
              return valuesList.map((val, i) => {
                const IconComp = getIconComponent(val.icon);
                return (
                  <AnimatedSection key={val.title + i} delay={i*100}>
                    <div className="text-center p-8 rounded-2xl bg-white border border-gray-100 hover:shadow-card transition-all duration-300">
                      <div className={`w-16 h-16 ${val.color==="gold"?"bg-accent-goldLight":"bg-brand-pale"} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                        <IconComp className={`w-8 h-8 ${val.color==="gold"?"text-accent-gold":"text-brand-primary"}`}/>
                      </div>
                      <h3 className="font-heading font-bold text-gray-900 mb-2">{val.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{val.desc}</p>
                    </div>
                  </AnimatedSection>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* ── REGISTERED OFFICE ────────────────────────── */}
      <section className="section-pad bg-brand-pale/40 border-y border-gray-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection direction="left">
              <SectionLabel text="Corporate Headquarters" color="gold" />
              <h2 className="section-heading mb-6">
                {g("registered_office", "title", "Registered Office & Global Hub")}
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base">
                <div
                  className="rich-content"
                  dangerouslySetInnerHTML={{
                    __html: g(
                      "registered_office",
                      "info",
                      "Sava Fresh Trading FZE is officially registered and licensed in the Jebel Ali Free Zone Authority (JAFZA), Dubai, United Arab Emirates — the strategic nexus of global maritime trade and international commerce."
                    )
                  }}
                />
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-soft space-y-3 mt-6">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">📍</span>
                    <div>
                      <h4 className="font-heading font-bold text-gray-900 text-sm">Official Address</h4>
                      <p className="text-gray-500 text-sm mt-0.5">
                        {g(
                          "registered_office",
                          "address",
                          "Premises No. S101230101WS90, Jebel Ali Free Zone Authority (JAFZA), Dubai, United Arab Emirates"
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100 text-xs text-brand-primary font-semibold">
                    <CheckCircle className="w-4 h-4 text-brand-primary" />
                    <span>JAFZA Licensed & Regulated Entity • Global Supply Chain Logistics</span>
                  </div>
                </div>
                <div className="pt-4">
                  <Link to="/contact" className="btn-primary">
                    Contact Registered Office <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection direction="right" delay={200}>
              <div className="relative rounded-3xl overflow-hidden shadow-hover border-4 border-white bg-white group aspect-[4/3] lg:aspect-[1.2]">
                <img
                  src={getImageUrl(
                    g(
                      "registered_office",
                      "imageUrl",
                      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80"
                    )
                  )}
                  alt="Sava Fresh Registered Office"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Certifications Module */}
      {certs.length > 0 && (
        <section className="section-pad bg-gray-section">
          <div className="container-custom">
            <AnimatedSection className="text-center mb-12">
              <SectionLabel text="Compliance & Standards" />
              <h2 className="section-heading">Our Certifications</h2>
              <p className="section-subheading mx-auto">
                We are fully accredited by leading international standards boards to guarantee safety, hygiene, and traceability.
              </p>
            </AnimatedSection>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {certs.map((cert, i) => (
                <AnimatedSection key={cert.id} delay={i * 60}>
                  <div
                    onClick={() => setViewCertItem(cert)}
                    className="bg-white rounded-3xl p-6 shadow-card hover:shadow-hover hover:-translate-y-1 transition-all duration-300 border border-gray-100 flex flex-col items-center justify-between cursor-pointer text-center h-full group"
                  >
                    <div className="w-20 h-20 bg-brand-pale rounded-2xl flex items-center justify-center overflow-hidden mb-4 p-2 border border-gray-50">
                      {cert.logoUrl ? (
                        <img
                          src={cert.logoUrl}
                          alt={cert.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <span className="text-3xl">🏆</span>
                      )}
                    </div>
                    <h3 className="font-heading font-bold text-gray-900 text-sm mb-1 line-clamp-2">{cert.name}</h3>
                    {cert.description && (
                      <p className="text-gray-400 text-xs line-clamp-2 mt-1">{cert.description.replace(/<[^>]+>/g, "")}</p>
                    )}
                    <span className="text-brand-primary text-xs font-semibold mt-3 flex items-center gap-1 group-hover:gap-1.5 transition-all">
                      View Certificate <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-brand-primary">
        <div className="container-custom text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">{g("cta", "heading", "Partner With Sava Fresh")}</h2>
            {(() => {
              const ctaDesc = g("cta", "description", "Join our growing network of global buyers and suppliers. Experience the difference that quality, reliability, and expertise can make for your business.");
              return ctaDesc.trim().startsWith("<") ? (
                <div className="text-brand-muted mb-8 max-w-xl mx-auto wysiwyg-output" dangerouslySetInnerHTML={{ __html: ctaDesc }} />
              ) : (
                <p className="text-brand-muted mb-8 max-w-xl mx-auto">{ctaDesc}</p>
              );
            })()}
            <Link to={g("cta", "btn_link", "/contact")} className="bg-white text-brand-primary px-8 py-4 rounded-xl font-semibold hover:bg-brand-pale transition-all inline-flex items-center gap-2">
              {g("cta", "btn_txt", "Get in Touch")} <ArrowRight className="w-4 h-4"/>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Lightbox / Certificate modal */}
      {viewCertItem && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={() => setViewCertItem(null)}>
          <div className="max-w-xl w-full bg-white rounded-3xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="relative p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-heading font-bold text-gray-900 text-lg">{viewCertItem.name}</h3>
              <button onClick={() => setViewCertItem(null)} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
              {viewCertItem.imageUrl ? (
                <img src={getImageUrl(viewCertItem.imageUrl)} alt={viewCertItem.name} className="w-full h-auto max-h-96 object-contain rounded-2xl border border-gray-100 p-2 bg-gray-50" />
              ) : (
                viewCertItem.logoUrl && (
                  <img src={getImageUrl(viewCertItem.logoUrl)} alt={viewCertItem.name} className="w-48 h-48 mx-auto object-contain p-4 bg-gray-50 rounded-2xl" />
                )
              )}
              {viewCertItem.description && (
                <div className="text-gray-600 text-sm leading-relaxed bg-gray-50 p-4 rounded-2xl rich-content" dangerouslySetInnerHTML={{ __html: viewCertItem.description }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Services Page ─────────────────────────────────────────
export function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cms, setCms] = useState({});

  useEffect(() => {
    Promise.all([
      servicesAPI.getAll().then(r => setServices(r.data.services || [])),
      cmsAPI.getPage("services").then(r => setCms(r.data.content || {})),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const g = (s, k, fb) => cms?.[s]?.[k]?.value || fb;

  return (
    <div className="pt-20">
      <Seo page="services" defaultTitle="Services — Sava Fresh Trading FZE" defaultDesc="End-to-end food trading services: global sourcing, quality control, logistics, and trade finance." />
      <PageBanner page="services" fallbackClass="from-brand-pale via-white to-accent-tealLight">
        <AnimatedSection className="max-w-2xl">
          <SectionLabel text="Our Services" />
          <h1 className="section-heading mb-4">{g("hero","heading","End-to-End Food Trading Solutions")}</h1>
          <div className="text-gray-600 leading-relaxed text-lg rich-content" dangerouslySetInnerHTML={{ __html: g("hero","description","From sourcing and procurement to logistics and compliance — Sava Fresh offers comprehensive food trading services that cover every aspect of the international supply chain. Our experienced team handles the complexity so you can focus on your core business. We provide customized solutions for importers, exporters, retailers, and food manufacturers worldwide.") }} />
        </AnimatedSection>
      </PageBanner>
      <section className="section-pad">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(6)].map((_,i)=><div key={i} className="bg-gray-100 rounded-2xl h-64 animate-pulse"/>)}</div>
          ) : services.length === 0 ? (
            <div className="text-center py-20"><p className="text-gray-400">No services found. Add services from the admin panel.</p></div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((svc,i)=>(
                <AnimatedSection key={svc.id} delay={i*80}>
                  <div className="bg-white rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden group border border-transparent hover:border-brand-muted h-full flex flex-col">
                    <div className="h-48 bg-brand-pale overflow-hidden flex items-center justify-center">
                      {svc.imageUrl ? <img src={getImageUrl(svc.imageUrl)} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/> : <div className="w-16 h-16 bg-brand-muted rounded-2xl flex items-center justify-center"><Award className="w-8 h-8 text-brand-primary"/></div>}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-heading font-bold text-gray-900 text-xl mb-2">{svc.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed flex-1">{svc.excerpt||svc.description?.replace(/<[^>]+>/g,"").slice(0,150)+"..."}</p>
                      <Link to={`/services/${svc.slug||svc.id}`} className="mt-4 btn-primary text-xs px-4 py-2.5 self-start">Learn More <ArrowRight className="w-3.5 h-3.5"/></Link>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Enhanced Section */}
      {g("enhanced_section", "heading", "") && (
        <section className="section-pad bg-brand-pale/30 border-t border-gray-100">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {g("enhanced_section", "imageUrl", "") && (
                <AnimatedSection direction="left">
                  <div className="relative rounded-3xl overflow-hidden shadow-card border-4 border-white bg-white">
                    <img
                      src={getImageUrl(g("enhanced_section", "imageUrl", ""))}
                      alt={g("enhanced_section", "heading", "")}
                      className="w-full h-auto object-cover max-h-[450px]"
                    />
                  </div>
                </AnimatedSection>
              )}
              <AnimatedSection direction="right" className={`space-y-6 ${g("enhanced_section", "imageUrl", "") ? "" : "lg:col-span-2 max-w-3xl mx-auto text-center"}`}>
                <SectionLabel text="Enhanced Service Focus" />
                <h2 className="section-heading">{g("enhanced_section", "heading", "")}</h2>
                <div
                  className="rich-content text-gray-600 leading-relaxed text-sm"
                  dangerouslySetInnerHTML={{ __html: g("enhanced_section", "content", "") }}
                />
                {g("enhanced_section", "cta_text", "") && (
                  <div className={g("enhanced_section", "imageUrl", "") ? "" : "text-center pt-4"}>
                    <a
                      href={g("enhanced_section", "cta_link", "") || "/contact"}
                      className="btn-primary inline-flex"
                    >
                      {g("enhanced_section", "cta_text", "")} <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </AnimatedSection>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Service Detail ────────────────────────────────────────
export function ServiceDetailPage() {
  const { slug } = useParams();
  const [svc, setSvc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    servicesAPI.getBySlug(slug).then(r=>setSvc(r.data.service)).catch(()=>setSvc(null)).finally(()=>setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="loader-ring"/></div>;
  if (!svc) return <div className="min-h-screen flex flex-col items-center justify-center pt-20 gap-4"><p className="text-gray-400">Service not found.</p><Link to="/services" className="btn-primary">Back to Services</Link></div>;

  return (
    <div className="pt-20">
      <Seo page="services" fallbackObj={svc} />
      <section className="bg-brand-pale py-16"><div className="container-custom max-w-3xl"><AnimatedSection><SectionLabel text="Our Services"/><h1 className="section-heading mb-3">{svc.title}</h1>{svc.excerpt&&<p className="text-gray-600 text-lg leading-relaxed">{svc.excerpt}</p>}</AnimatedSection></div></section>
      <section className="section-pad">
        <div className="container-custom max-w-3xl">
          {svc.imageUrl&&<img src={getImageUrl(svc.imageUrl)} alt={svc.title} className="w-full h-64 object-cover rounded-2xl mb-8 shadow-card"/>}
          <div className="rich-content" dangerouslySetInnerHTML={{__html:svc.description||svc.content||""}}/>
          <div className="mt-10 pt-6 border-t border-gray-100 flex gap-4">
            <Link to="/services" className="btn-outline">← All Services</Link>
            <Link to="/contact" className="btn-primary">Get in Touch <ArrowRight className="w-4 h-4"/></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ── Privacy Policy ────────────────────────────────────────
export function PrivacyPage() {
  const [cms, setCms] = useState({});
  useEffect(()=>{cmsAPI.getPage("privacy").then(r=>setCms(r.data.content||{})).catch(()=>{});},[]);
  const content = cms?.main?.content?.value || `<h2>Privacy Policy</h2><p>Last updated: January 2024</p><p>At Sava Fresh Trading FZE ("we," "us," or "our"), we are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or contact us for business purposes.</p><h3>Information We Collect</h3><p>We collect information you provide directly to us, including your name, email address, phone number, company name, and any message content when you fill out our contact form or communicate with us via email or phone. We may also collect technical information such as IP address, browser type, and pages visited through standard web analytics tools.</p><h3>How We Use Your Information</h3><p>We use the information we collect to: respond to your inquiries and provide requested services; send you product information, pricing, and quotations; improve our website and business operations; comply with legal obligations; and prevent fraudulent activities. We do not sell your personal information to third parties under any circumstances.</p><h3>Data Retention</h3><p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, typically for a period of five years for business communications and seven years for transactional records as required by UAE law.</p><h3>Contact Us</h3><p>If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us at info@savafresh.com.</p>`;
  return (
    <div className="pt-20">
      <Seo page="privacy" defaultTitle="Privacy Policy — Sava Fresh Trading FZE" />
      <section className="bg-brand-pale py-16"><div className="container-custom"><AnimatedSection className="max-w-2xl"><SectionLabel text="Legal"/><h1 className="section-heading">Privacy Policy</h1></AnimatedSection></div></section>
      <section className="section-pad"><div className="container-custom max-w-3xl"><div className="rich-content" dangerouslySetInnerHTML={{__html:content}}/></div></section>
    </div>
  );
}

// ── Terms ─────────────────────────────────────────────────
export function TermsPage() {
  const [cms, setCms] = useState({});
  useEffect(()=>{cmsAPI.getPage("terms").then(r=>setCms(r.data.content||{})).catch(()=>{});},[]);
  const content = cms?.main?.content?.value || `<h2>Terms and Conditions</h2><p>Last updated: January 2024</p><p>By accessing and using the Sava Fresh Trading FZE website (savafresh.com), you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our website. These terms apply to all visitors, users, and others who access or use our services.</p><h3>Use of Website</h3><p>This website is provided for informational and business purposes. All content, including text, images, logos, and data, is the intellectual property of Sava Fresh Trading FZE unless otherwise stated. You may not reproduce, distribute, or create derivative works from our content without prior written permission.</p><h3>Product Information and Pricing</h3><p>Product specifications, availability, and pricing shown on this website are subject to change without prior notice. All prices quoted are indicative and subject to confirmation at the time of order. Actual pricing may vary based on quantity, destination, packaging requirements, and market conditions.</p><h3>Limitation of Liability</h3><p>Sava Fresh Trading FZE shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of this website or our services, to the maximum extent permitted by applicable UAE law.</p><h3>Governing Law</h3><p>These Terms and Conditions are governed by the laws of the United Arab Emirates. Any disputes shall be subject to the exclusive jurisdiction of the courts of Sharjah, UAE.</p><h3>Contact</h3><p>For questions regarding these terms, contact us at info@savafresh.com.</p>`;
  return (
    <div className="pt-20">
      <Seo page="terms" defaultTitle="Terms & Conditions — Sava Fresh Trading FZE" />
      <section className="bg-brand-pale py-16"><div className="container-custom"><AnimatedSection className="max-w-2xl"><SectionLabel text="Legal"/><h1 className="section-heading">Terms & Conditions</h1></AnimatedSection></div></section>
      <section className="section-pad"><div className="container-custom max-w-3xl"><div className="rich-content" dangerouslySetInnerHTML={{__html:content}}/></div></section>
    </div>
  );
}
